import { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Stack,
  CircularProgress,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import useBackendApi from '../hooks/useBackendApi';
import { useThemeContext } from '../contexts/BaseThemeContextProvider';

const backendURL = process.env.REACT_APP_BACKEND_URL as string;

interface AddVitalsFormProps {
  orgPathName: string;
  accPathName: string;
  patientUserId: string;
  onSuccess: () => void;
  onCancel: () => void;
  isDialog?: boolean;
  onSubmittingChange?: (isSubmitting: boolean) => void;
  eventId?: number | null;
}

export default function AddVitalsForm({
  orgPathName,
  accPathName,
  patientUserId,
  onSuccess,
  onCancel,
  isDialog,
  onSubmittingChange,
  eventId,
}: AddVitalsFormProps) {
  const { theme } = useThemeContext();
  const { apiFetchRequest, isLoading } = useBackendApi();

  const [systolic, setSystolic] = useState('');
  const [diastolic, setDiastolic] = useState('');
  const [bpm, setBpm] = useState('');
  const [errorText, setErrorText] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorText(null);

    const s = parseFloat(systolic);
    const d = parseFloat(diastolic);
    const b = bpm ? parseFloat(bpm) : undefined;

    if (isNaN(s) || isNaN(d) || (bpm && isNaN(b as number))) {
      setErrorText('Please enter valid numeric values.');
      return;
    }

    onSubmittingChange?.(true);

    try {
      const endpoint = `${backendURL}/o/${encodeURIComponent(orgPathName)}/a/${encodeURIComponent(
        accPathName,
      )}/patients/${encodeURIComponent(patientUserId)}/vitals`;

      const res = await apiFetchRequest({
        endpoint,
        method: 'POST',
        body: { systolic: s, diastolic: d, bpm: b, ...(eventId ? { event_id: eventId } : {}) },
      });

      if (res?.status === 200) {
        onSuccess();
      } else {
        setErrorText('Failed to save vitals. Please try again.');
      }
    } catch {
      setErrorText('An error occurred. Please try again.');
    } finally {
      onSubmittingChange?.(false);
    }
  };

  const formContent = (
    <Stack spacing={3} sx={{ mt: isDialog ? 1 : 0 }}>
      {errorText && <Typography color="error">{errorText}</Typography>}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          label="Systolic (mmHg)"
          variant="outlined"
          fullWidth
          required
          type="number"
          value={systolic}
          onChange={(e) => setSystolic(e.target.value)}
        />
        <TextField
          label="Diastolic (mmHg)"
          variant="outlined"
          fullWidth
          required
          type="number"
          value={diastolic}
          onChange={(e) => setDiastolic(e.target.value)}
        />
      </Stack>

      <TextField
        label="Heart Rate (BPM)"
        variant="outlined"
        fullWidth
        type="number"
        value={bpm}
        onChange={(e) => setBpm(e.target.value)}
      />

      {!isDialog && (
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button variant="outlined" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button variant="contained" type="submit" disabled={isLoading}>
            {isLoading ? <CircularProgress size={24} /> : 'Save Vitals'}
          </Button>
        </Stack>
      )}
    </Stack>
  );

  if (isDialog) {
    return (
      <form onSubmit={handleSubmit}>
        <DialogTitle>Add Vitals</DialogTitle>
        <DialogContent>{formContent}</DialogContent>
        <DialogActions>
          <Button onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button variant="contained" type="submit" disabled={isLoading}>
            {isLoading ? <CircularProgress size={24} /> : 'Save Vitals'}
          </Button>
        </DialogActions>
      </form>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: theme.custom.pages.frost.authHome.cards.backgroundColor,
        borderRadius: '16px',
        padding: { xs: '20px', sm: '28px' },
        border: '1px solid',
        borderColor: theme.custom.pages.frost.authHome.cards.borderColor,
      }}
    >
      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          color: theme.custom.pages.frost.authHome.typography.body1.color,
          marginBottom: '16px',
        }}
      >
        Add Vitals
      </Typography>

      <form onSubmit={handleSubmit}>{formContent}</form>
    </Box>
  );
}
